import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

// CSS deliberately mirrors Employees.js/Payroll.js's tokens and .adm-*
// classes 1:1 so this reads as the same module, not a bolted-on page —
// with a small set of new severity tokens (--danger/--warn/--caution/--info)
// added specifically for this page's whole reason for existing: showing at
// a glance how urgent an expiry is.
const CSS = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg: #f0f4fc; --surface: #ffffff; --surface2: #f4f8ff;
  --border: #c0d0eb; --border2: #d5e3f5;
  --accent: #0284c7; --text: #0d1a36; --text2: #2a4a78; --muted: #6080aa;
  --danger: #dc2626; --danger-bg: rgba(220,38,38,.07); --danger-border: rgba(220,38,38,.22);
  --warn: #b45309; --warn-bg: rgba(180,83,9,.07); --warn-border: rgba(180,83,9,.22);
  --caution: #a16207; --caution-bg: rgba(161,98,7,.07); --caution-border: rgba(161,98,7,.22);
  --info: #0284c7; --info-bg: rgba(2,132,199,.07); --info-border: rgba(2,132,199,.22);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color-scheme: light;
}
body { background: var(--bg); color: var(--text); }
.adm-topbar { position: sticky; top: 0; z-index: 100; height: 52px; display: flex; align-items: center; background: rgba(240,244,252,.92); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border2); }
.adm-back { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; background: none; border: none; border-right: 1px solid var(--border2); color: var(--text2); cursor: pointer; font-size: 16px; flex-shrink: 0; transition: color .15s, background .15s; }
.adm-back:hover { color: var(--text); background: var(--surface); }
.adm-brand { display: flex; align-items: center; gap: 10px; padding: 0 20px; flex: 1; }
.adm-brand-mark { width: 26px; height: 26px; border-radius: 6px; background: linear-gradient(135deg, #0284c7, #2563eb); display: flex; align-items: center; justify-content: center; font-size: 13px; }
.adm-brand-label { font-size: 14px; font-weight: 600; color: var(--text); }
.adm-avatar { margin-right: 16px; width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #7c3aed); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; }
.adm-tabs { display: flex; align-items: center; gap: 2px; padding: 0 20px; border-bottom: 1px solid var(--border2); background: var(--surface); overflow-x: auto; }
.adm-tab { padding: 14px 16px; font-size: 13px; font-weight: 500; color: var(--muted); background: none; border: none; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color .15s; font-family: inherit; white-space: nowrap; }
.adm-tab:hover { color: var(--text2); }
.adm-tab.active { color: var(--accent); font-weight: 700; border-bottom-color: var(--accent); }
.adm-main { max-width: 1200px; margin: 0 auto; padding: 28px 24px 80px; }
.adm-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 24px; margin-bottom: 14px; box-shadow: 0 1px 2px rgba(13,26,54,.04), 0 4px 12px rgba(13,26,54,.05); }
.adm-card-title { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
.adm-card-desc { font-size: 12px; color: var(--muted); margin-bottom: 16px; line-height: 1.5; }
.adm-error { padding: 10px 14px; border-radius: 7px; background: rgba(248,113,113,.08); border: 1px solid rgba(248,113,113,.2); color: #dc2626; font-size: 13px; margin-bottom: 14px; }
.adm-spinner-wrap { display: flex; justify-content: center; padding: 40px 0; }
.adm-spinner { width: 26px; height: 26px; border-radius: 50%; border: 3px solid var(--border2); border-top-color: var(--accent); animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.adm-empty { font-size: 13px; color: var(--muted); padding: 30px 20px; text-align: center; border: 1px dashed var(--border2); border-radius: 8px; background: var(--surface2); }
.adm-input { background: var(--surface2); border: 1px solid var(--border2); border-radius: 7px; padding: 9px 12px; font-size: 13px; color: var(--text); outline: none; font-family: inherit; transition: border-color .15s; }
.adm-input:focus { border-color: var(--accent); }
.adm-select { background: var(--surface2); border: 1px solid var(--border2); border-radius: 7px; padding: 9px 12px; font-size: 13px; color: var(--text); outline: none; font-family: inherit; cursor: pointer; }
.adm-action-btn { background: none; border: 1px solid var(--border); border-radius: 6px; padding: 5px 11px; font-size: 12px; color: var(--text2); cursor: pointer; font-family: inherit; transition: border-color .15s, color .15s; white-space: nowrap; }
.adm-action-btn:hover { border-color: var(--accent); color: var(--accent); }
.adm-action-btn.active { border-color: var(--accent); color: var(--accent); background: rgba(2,132,199,.06); }

.exp-page-head { display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: 10px; margin-bottom: 20px; }
.exp-page-head h1 { font-family: inherit; font-size: 20px; font-weight: 700; color: var(--text); }
.exp-page-head p { font-size: 12.5px; color: var(--muted); margin-top: 3px; }
.exp-asof { font-size: 11px; color: var(--muted); font-variant-numeric: tabular-nums; }

.exp-tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 22px; }
@media(max-width: 760px) { .exp-tiles { grid-template-columns: 1fr 1fr; } }
.exp-tile { position: relative; text-align: left; background: var(--surface); border: 1px solid var(--border); border-left: 4px solid var(--tile-c); border-radius: 9px; padding: 16px 16px 14px; cursor: pointer; font-family: inherit; transition: transform .12s, box-shadow .12s; }
.exp-tile:hover { transform: translateY(-1px); box-shadow: 0 6px 16px -8px rgba(13,26,54,.18); }
.exp-tile.active { box-shadow: 0 0 0 2px var(--tile-c); }
.exp-tile .n { font-size: 26px; font-weight: 800; color: var(--tile-c); line-height: 1; font-variant-numeric: tabular-nums; }
.exp-tile .l { font-size: 11.5px; font-weight: 600; color: var(--text2); margin-top: 7px; text-transform: uppercase; letter-spacing: .03em; }
.exp-tile .s { font-size: 11px; color: var(--muted); margin-top: 2px; }

.exp-filters { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 16px; }
.exp-filters .adm-input { flex: 1; min-width: 200px; }

.exp-table-wrap { overflow-x: auto; }
.exp-table { width: 100%; border-collapse: collapse; min-width: 640px; }
.exp-table th { font-size: 10px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--muted); padding: 0 14px 10px; text-align: left; border-bottom: 1px solid var(--border2); white-space: nowrap; }
.exp-table td { padding: 12px 14px; font-size: 13px; color: var(--text2); border-bottom: 1px solid var(--border2); vertical-align: middle; }
.exp-table tr:last-child td { border-bottom: none; }
.exp-table tr:hover td { background: var(--surface2); }
.exp-table .emp-name { color: var(--text); font-weight: 600; }
.exp-table .emp-code { color: var(--muted); font-weight: 500; }
.exp-days { font-variant-numeric: tabular-nums; }

.exp-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 20px; font-size: 10.5px; font-weight: 700; letter-spacing: .02em; border: 1px solid; white-space: nowrap; }
.exp-badge-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
`;

// Ordered soonest-to-least-urgent, matching how the backend already groups
// items — used both for tile order and for flattening "all" into one list.
const BUCKETS = [
  { key: "expired",     label: "Already Expired",   sub: "past due",        color: "var(--danger)",  bg: "var(--danger-bg)",  border: "var(--danger-border)"  },
  { key: "expiring_30", label: "Expiring in 30 Days", sub: "act soon",      color: "var(--warn)",    bg: "var(--warn-bg)",    border: "var(--warn-border)"    },
  { key: "expiring_60", label: "Expiring in 60 Days", sub: "keep an eye",   color: "var(--caution)", bg: "var(--caution-bg)", border: "var(--caution-border)" },
  { key: "expiring_90", label: "Expiring in 90 Days", sub: "on the radar",  color: "var(--info)",    bg: "var(--info-bg)",    border: "var(--info-border)"    },
];

const DOCUMENT_TYPE_OPTIONS = [
  { value: "", label: "All document types" },
  { value: "civil_id_front", label: "Civil ID" },
  { value: "passport", label: "Passport" },
  { value: "foreign_id", label: "Foreign ID" },
  { value: "residence_permit", label: "Residence Permit" },
  { value: "employment_contract", label: "Employment Contract" },
];

function bucketMeta(key) {
  return BUCKETS.find(b => b.key === key);
}

function daysLabel(d) {
  if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} overdue`;
  if (d === 0) return "expires today";
  return `in ${d} day${d === 1 ? "" : "s"}`;
}

function IconArrowLeft(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M11 18l-6-6 6-6" />
    </svg>
  );
}

export default function DocumentExpiry() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "H").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [activeBucket, setActiveBucket] = useState(null); // null = show all four urgency buckets combined
  const [docTypeFilter, setDocTypeFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/employee-documents/expiring");
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Failed to load document expiry data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const bucketKeys = activeBucket ? [activeBucket] : ["expired", "expiring_30", "expiring_60", "expiring_90"];
    let out = bucketKeys.flatMap(k => (data.items[k] || []).map(item => ({ ...item, bucket: k })));
    if (docTypeFilter) {
      out = out.filter(r => docTypeFilter === "civil_id_front" ? r.document_type.startsWith("civil_id") : r.document_type === docTypeFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(r => r.employee_name.toLowerCase().includes(q) || r.employee_code.toLowerCase().includes(q));
    }
    return out;
  }, [data, activeBucket, docTypeFilter, search]);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" />
      <style>{CSS}</style>
      <header className="adm-topbar">
        <button className="adm-back" onClick={() => navigate("/")} title="Back"><IconArrowLeft /></button>
        <div className="adm-brand">
          <div className="adm-brand-mark">📋</div>
          <span className="adm-brand-label">HRMS & Payroll — Document Expiry</span>
        </div>
        <div style={{ marginRight: 8, fontSize: 12, color: "#6080aa" }}>{user?.org_name}</div>
        <div className="adm-avatar" title={user?.name}>{initials}</div>
      </header>

      <div className="adm-tabs">
        <button className="adm-tab" onClick={() => navigate("/hrms/dashboard")}>Dashboard</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/employees")}>Employees</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/payroll")}>Payroll</button>
        <button className="adm-tab active">Document Expiry</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/data-quality")}>Data Quality</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/company-documents")}>Company Documents</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/assistant")}>AI Assistant</button>
      </div>

      <div className="adm-main">
        {error && <div className="adm-error">{error}</div>}

        <div className="exp-page-head">
          <div>
            <h1>Document Expiry Tracking</h1>
            <p>Civil IDs, passports, residence permits and employment contracts across every employee, sorted by urgency.</p>
          </div>
          {data && <div className="exp-asof">As of {new Date(data.generated_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>}
        </div>

        {loading ? (
          <div className="adm-spinner-wrap"><div className="adm-spinner" /></div>
        ) : data && (
          <>
            <div className="exp-tiles">
              {BUCKETS.map(b => (
                <button key={b.key} type="button"
                        className={`exp-tile${activeBucket === b.key ? " active" : ""}`}
                        style={{ "--tile-c": b.color }}
                        onClick={() => setActiveBucket(prev => prev === b.key ? null : b.key)}>
                  <div className="n">{data.counts[b.key]}</div>
                  <div className="l">{b.label}</div>
                  <div className="s">{b.sub} — click to {activeBucket === b.key ? "clear filter" : "filter"}</div>
                </button>
              ))}
            </div>

            <div className="adm-card">
              <div className="adm-card-title">
                {activeBucket ? bucketMeta(activeBucket).label : "All Upcoming Expiries"} ({rows.length})
              </div>
              <div className="adm-card-desc">
                Civil ID / Passport / Foreign ID / Residence Permit come from HR-approved document data. Employment Contract comes from the contract end date on the employee's record.
              </div>

              <div className="exp-filters">
                <input className="adm-input" placeholder="Search by employee name or code…"
                       value={search} onChange={e => setSearch(e.target.value)} />
                <select className="adm-select" value={docTypeFilter} onChange={e => setDocTypeFilter(e.target.value)}>
                  {DOCUMENT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {activeBucket && (
                  <button className="adm-action-btn" onClick={() => setActiveBucket(null)}>Clear urgency filter</button>
                )}
              </div>

              {rows.length === 0 ? (
                <div className="adm-empty">
                  {activeBucket || docTypeFilter || search
                    ? "Nothing matches this filter."
                    : "Nothing expiring in the next 90 days, and nothing already expired. All clear."}
                </div>
              ) : (
                <div className="exp-table-wrap">
                  <table className="exp-table">
                    <thead>
                      <tr><th>Employee</th><th>Document</th><th>Expiry Date</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const m = bucketMeta(r.bucket);
                        return (
                          <tr key={`${r.document_id || "contract"}-${r.employee_id}-${i}`}>
                            <td>
                              <div className="emp-name">{r.employee_name}</div>
                              <div className="emp-code">{r.employee_code}</div>
                            </td>
                            <td>{r.document_label}</td>
                            <td className="exp-days">{r.expiry_date}</td>
                            <td>
                              <span className="exp-badge" style={{ color: m.color, background: m.bg, borderColor: m.border }}>
                                <span className="exp-badge-dot" />{daysLabel(r.days_remaining)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
